function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { fmtClock as fmt } from "./sequences-api";

/*
 * Clip editor timeline (2026-10-08 redesign, PlunkDocs mockups/video-editors-redesign-2026-10-08.html screens 1 and 2).
 *
 * Frames: grabbed in the browser from a separate muted <video crossOrigin="anonymous"> (seek, draw to a canvas). When the
 * file is not served with CORS headers the load errors or the canvas is tainted; then the strip falls back to the
 * version's poster repeated, or plain tinted blocks. Nothing here can throw into the page: every failure resolves null.
 */

const FRAME_CACHE = new Map();
function seekTo(v, t) {
  return new Promise((ok, no) => {
    let timer = null;
    const done = () => {
      clearTimeout(timer);
      v.removeEventListener("seeked", done);
      ok();
    };
    timer = setTimeout(() => {
      v.removeEventListener("seeked", done);
      no(new Error("seek timed out"));
    }, 6000);
    v.addEventListener("seeked", done);
    try {
      v.currentTime = t;
    } catch (e) {
      clearTimeout(timer);
      no(e);
    }
  });
}

/** Opens the file in a hidden CORS video and hands it to `work(video)`; resolves work's result, or null on any failure. */
function withVideo(url, work, limitMs) {
  return new Promise(resolve => {
    if (!url || typeof document === "undefined") {
      resolve(null);
      return;
    }
    let finished = false;
    const v = document.createElement("video");
    const finish = r => {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      try {
        v.removeAttribute("src");
        v.load();
      } catch (e) {/* already gone */}
      resolve(r);
    };
    const timer = setTimeout(() => finish(null), limitMs || 30000);
    v.crossOrigin = "anonymous";
    v.muted = true;
    v.playsInline = true;
    v.preload = "auto";
    v.onerror = () => finish(null);
    v.onloadeddata = async () => {
      v.onloadeddata = null;
      try {
        finish(await work(v));
      } catch (e) {
        finish(e && e.taint ? {
          tainted: true
        } : null);
      }
    };
    try {
      v.src = url;
    } catch (e) {
      finish(null);
    }
  });
}

/** `count` small frames spread across the clip as data URLs, or null (no CORS, tainted canvas, slow network). Cached per file. */
export function grabFrames(url, count) {
  const key = `${url}|${count}`;
  if (FRAME_CACHE.has(key)) return FRAME_CACHE.get(key);
  const p = withVideo(url, async v => {
    const d = v.duration;
    if (!isFinite(d) || d <= 0) return null;
    const w = 192;
    const h = Math.max(48, Math.round(w * ((v.videoHeight || 9) / (v.videoWidth || 16))));
    const c = document.createElement("canvas");
    c.width = w;
    c.height = h;
    const g = c.getContext("2d");
    if (!g) return null;
    const out = [];
    for (let i = 0; i < count; i++) {
      await seekTo(v, Math.min(Math.max(0, d - 0.05), (i + 0.5) * d / count));
      g.drawImage(v, 0, 0, w, h);
      try {
        out.push(c.toDataURL("image/jpeg", 0.72));
      } catch (e) {
        return null;
      }
    }
    return out;
  }, 40000).then(r => Array.isArray(r) ? r : null);
  FRAME_CACHE.set(key, p);
  return p;
}

/** One full-size frame at `t` seconds as a JPEG blob. Rejects with { tainted: true } when the browser may not read the pixels. */
export async function captureFrame(url, t) {
  const r = await withVideo(url, async v => {
    const d = isFinite(v.duration) ? v.duration : 0;
    await seekTo(v, Math.min(Math.max(0, Number(t) || 0), Math.max(0, d - 0.05)));
    const c = document.createElement("canvas");
    c.width = v.videoWidth || 1280;
    c.height = v.videoHeight || 720;
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    return await new Promise((ok, no) => {
      try {
        c.toBlob(b => b ? ok(b) : no(new Error("no frame")), "image/jpeg", 0.9);
      } catch (e) {
        const er = new Error("tainted");
        er.taint = true;
        no(er);
      }
    });
  }, 30000);
  if (r && r.tainted) {
    const e = new Error("This video cannot be read in the browser.");
    e.tainted = true;
    throw e;
  }
  if (!r) {
    const e = new Error("The frame could not be read from this video.");
    e.tainted = true;
    throw e;
  }
  return r;
}
const clamp = (x, a, b) => Math.max(a, Math.min(b, x));
const round1 = x => Math.round(x * 10) / 10;

/** The strip of frames. `frames` (data URLs) when grabbed, else the poster repeated, else tinted blocks. */
export function FrameStrip({
  frames,
  poster,
  count,
  className,
  style
}) {
  const n = count || 10;
  return /*#__PURE__*/React.createElement("div", {
    className: "cx-frames " + (className || ""),
    style: {
      gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))`,
      ...(style || {})
    },
    "aria-hidden": "true"
  }, Array.from({
    length: n
  }).map((_, i) => {
    if (frames && frames[i]) return /*#__PURE__*/React.createElement("img", {
      key: i,
      src: frames[i],
      alt: "",
      draggable: false
    });
    if (poster) return /*#__PURE__*/React.createElement("img", {
      key: i,
      src: poster,
      alt: "",
      draggable: false,
      style: {
        objectPosition: `${Math.round(i / Math.max(1, n - 1) * 100)}% 50%`
      }
    });
    return /*#__PURE__*/React.createElement("span", {
      key: i,
      className: "tint" + (i % 2 ? " alt" : "")
    });
  }));
}

/**
 * Trim timeline: play button and time, the frame strip with a handle at each end, the playhead and a time ruler.
 * Click the strip to jump; drag a handle (or focus it and use the arrow keys, Shift for whole seconds) to set the range.
 */
export class TrimTimeline extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "strip", /*#__PURE__*/React.createRef());
    _defineProperty(this, "drag", null);
    _defineProperty(this, "onStripDown", e => {
      if (!this.props.dur || e.button > 0) return;
      if (e.target && e.target.closest && e.target.closest(".cx-handle")) return;
      this.props.onSeek(round1(this.frac(e) * this.props.dur));
    });
    _defineProperty(this, "onHandleDown", which => e => {
      if (!this.props.dur || this.props.disabled) return;
      e.preventDefault();
      e.stopPropagation();
      this.drag = which;
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch (er) {/* older browsers */}
    });
    _defineProperty(this, "onHandleMove", e => {
      if (!this.drag) return;
      const {
        dur,
        start,
        end
      } = this.props;
      const t = round1(this.frac(e) * dur);
      if (this.drag === "start") this.props.onTrim(clamp(t, 0, Math.max(0, end - 0.5)), end, "start");else this.props.onTrim(start, clamp(t, Math.min(dur, start + 0.5), dur), "end");
    });
    _defineProperty(this, "onHandleUp", e => {
      if (!this.drag) return;
      this.drag = null;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch (er) {/* fine */}
      if (this.props.onTrimDone) this.props.onTrimDone();
    });
    _defineProperty(this, "onKey", which => e => {
      const {
        dur,
        start,
        end,
        disabled
      } = this.props;
      if (!dur || disabled) return;
      const step = e.shiftKey ? 1 : 0.1;
      let d = 0;
      if (e.key === "ArrowLeft" || e.key === "ArrowDown") d = -step;else if (e.key === "ArrowRight" || e.key === "ArrowUp") d = step;else return;
      e.preventDefault();
      if (which === "start") this.props.onTrim(round1(clamp(start + d, 0, end - 0.5)), end, "start");else this.props.onTrim(start, round1(clamp(end + d, start + 0.5, dur)), "end");
      if (this.props.onTrimDone) this.props.onTrimDone();
    });
  }
  frac(e) {
    const box = this.strip.current.getBoundingClientRect();
    return clamp((e.clientX - box.left) / Math.max(1, box.width), 0, 1);
  }
  render() {
    const {
      dur,
      time,
      start,
      end,
      playing,
      onPlay,
      frames,
      poster,
      count,
      ranges,
      disabled,
      hint
    } = this.props;
    const pct = x => `${dur ? clamp(x / dur * 100, 0, 100) : 0}%`;
    const trimmed = dur && (start > 0.05 || end < dur - 0.05);
    const ticks = dur ? [0, 0.25, 0.5, 0.75, 1].map(f => fmt(f * dur)) : [];
    return /*#__PURE__*/React.createElement("div", {
      className: "cx-tl"
    }, /*#__PURE__*/React.createElement("div", {
      className: "cx-tl-head"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "cx-play",
      "aria-label": playing ? "Pause" : "Play",
      title: playing ? "Pause" : "Play",
      onClick: onPlay,
      disabled: !dur
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, playing ? "pause" : "play_arrow")), /*#__PURE__*/React.createElement("span", {
      className: "cx-time"
    }, /*#__PURE__*/React.createElement("b", null, fmt(time)), " / ", fmt(dur), trimmed ? /*#__PURE__*/React.createElement("span", {
      className: "dim"
    }, " \xB7 keeping ", fmt(start), " to ", fmt(end), " (", fmt(end - start), ")") : null), /*#__PURE__*/React.createElement("span", {
      className: "sp"
    }), /*#__PURE__*/React.createElement("span", {
      className: "cx-tl-hint"
    }, hint || "Drag the ends to trim · click to jump")), /*#__PURE__*/React.createElement("div", {
      className: "cx-strip" + (disabled ? " off" : ""),
      ref: this.strip,
      onPointerDown: this.onStripDown
    }, /*#__PURE__*/React.createElement(FrameStrip, {
      frames: frames,
      poster: poster,
      count: count
    }), (ranges || []).map((r, i) => /*#__PURE__*/React.createElement("span", {
      key: i,
      className: "cx-range",
      style: {
        left: pct(r.start),
        width: `calc(${pct(r.end)} - ${pct(r.start)})`
      },
      title: r.label || ""
    })), /*#__PURE__*/React.createElement("span", {
      className: "cx-shade",
      style: {
        left: 0,
        width: pct(start)
      }
    }), /*#__PURE__*/React.createElement("span", {
      className: "cx-shade",
      style: {
        left: pct(end),
        right: 0
      }
    }), /*#__PURE__*/React.createElement("span", {
      className: "cx-keep",
      style: {
        left: pct(start),
        width: `calc(${pct(end)} - ${pct(start)})`
      }
    }), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "cx-handle start",
      style: {
        left: pct(start)
      },
      disabled: disabled || !dur,
      "aria-label": `Trim start, ${fmt(start)}`,
      title: "Drag to set where the clip starts",
      onPointerDown: this.onHandleDown("start"),
      onPointerMove: this.onHandleMove,
      onPointerUp: this.onHandleUp,
      onPointerCancel: this.onHandleUp,
      onKeyDown: this.onKey("start")
    }, /*#__PURE__*/React.createElement("i", null)), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "cx-handle end",
      style: {
        left: pct(end)
      },
      disabled: disabled || !dur,
      "aria-label": `Trim end, ${fmt(end)}`,
      title: "Drag to set where the clip ends",
      onPointerDown: this.onHandleDown("end"),
      onPointerMove: this.onHandleMove,
      onPointerUp: this.onHandleUp,
      onPointerCancel: this.onHandleUp,
      onKeyDown: this.onKey("end")
    }, /*#__PURE__*/React.createElement("i", null)), /*#__PURE__*/React.createElement("span", {
      className: "cx-head",
      style: {
        left: pct(time)
      }
    })), ticks.length ? /*#__PURE__*/React.createElement("div", {
      className: "cx-ticks"
    }, ticks.map((t, i) => /*#__PURE__*/React.createElement("span", {
      key: i
    }, t))) : null);
  }
}

/**
 * Cut timeline (Cut and caption open): the kept clips as blocks in cut order, sized by length, then what is left out.
 * Blocks are buttons (click selects and jumps); drag a block onto another to reorder. Captions and on-screen text run
 * underneath.
 */
export class CutTimeline extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      dragFrom: -1,
      over: -1
    });
  }
  frameAt(t) {
    const {
      frames,
      poster,
      dur,
      count
    } = this.props;
    if (frames && frames.length && dur) return frames[clamp(Math.floor(t / dur * (count || frames.length)), 0, frames.length - 1)];
    return poster || null;
  }
  render() {
    const {
      segments,
      sel,
      onSelect,
      onMove,
      dur,
      playing,
      onPlayCut,
      captions,
      cutTime
    } = this.props;
    const {
      dragFrom,
      over
    } = this.state;
    const lens = segments.map(s => Math.max(0.1, Number(s.end) - Number(s.start)));
    const total = lens.reduce((a, b) => a + b, 0);
    const left = Math.max(0, (dur || 0) - total);
    return /*#__PURE__*/React.createElement("div", {
      className: "cx-tl"
    }, /*#__PURE__*/React.createElement("div", {
      className: "cx-tl-head"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "cx-play",
      "aria-label": playing ? "Pause" : "Play the cut",
      title: playing ? "Pause" : "Play the cut",
      onClick: onPlayCut
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, playing ? "pause" : "play_arrow")), /*#__PURE__*/React.createElement("span", {
      className: "cx-time"
    }, playing ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("b", null, fmt(cutTime || 0)), " of ", fmt(total)) : /*#__PURE__*/React.createElement(React.Fragment, null, "The cut runs ", /*#__PURE__*/React.createElement("b", null, fmt(total)), dur ? ` of ${fmt(dur)}` : "")), /*#__PURE__*/React.createElement("span", {
      className: "sp"
    }), /*#__PURE__*/React.createElement("span", {
      className: "cx-tl-hint"
    }, "Drag clips to reorder \xB7 click a clip to edit it")), /*#__PURE__*/React.createElement("div", {
      className: "cx-cut"
    }, segments.map((s, i) => {
      const img = this.frameAt(Number(s.start) || 0);
      return /*#__PURE__*/React.createElement("button", {
        type: "button",
        key: i,
        draggable: true,
        className: "cx-block" + (i === sel ? " sel" : "") + (over === i && dragFrom !== i ? " over" : ""),
        style: {
          flex: `${lens[i]} 1 0`
        },
        title: `Clip ${i + 1} · ${fmt(s.start)} to ${fmt(s.end)}`,
        "aria-label": `Clip ${i + 1}, ${fmt(s.start)} to ${fmt(s.end)}`,
        "aria-pressed": i === sel,
        onClick: () => onSelect(i),
        onDragStart: e => {
          this.setState({
            dragFrom: i
          });
          try {
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("text/plain", String(i));
          } catch (er) {/* IE */}
        },
        onDragOver: e => {
          e.preventDefault();
          if (over !== i) this.setState({
            over: i
          });
        },
        onDragLeave: () => {
          if (over === i) this.setState({
            over: -1
          });
        },
        onDrop: e => {
          e.preventDefault();
          const from = dragFrom;
          this.setState({
            dragFrom: -1,
            over: -1
          });
          if (from >= 0 && from !== i) onMove(from, i);
        },
        onDragEnd: () => this.setState({
          dragFrom: -1,
          over: -1
        })
      }, img ? /*#__PURE__*/React.createElement("img", {
        src: img,
        alt: "",
        draggable: false
      }) : /*#__PURE__*/React.createElement("span", {
        className: "tint"
      }), /*#__PURE__*/React.createElement("b", null, i + 1));
    }), left >= 0.5 ? /*#__PURE__*/React.createElement("span", {
      className: "cx-leftout",
      style: {
        flex: `${left} 1 0`
      }
    }, fmt(left), " left out") : null), /*#__PURE__*/React.createElement("div", {
      className: "cx-caps"
    }, segments.map((s, i) => {
      const words = [s.overlay ? `Text: ${s.overlay}` : "", captions && s.caption ? `Caption: ${s.caption}` : ""].filter(Boolean).join(" · ");
      return /*#__PURE__*/React.createElement("span", {
        key: i,
        className: words ? "on" : "",
        style: {
          flex: `${lens[i]} 1 0`
        },
        title: words
      }, words);
    }), left >= 0.5 ? /*#__PURE__*/React.createElement("span", {
      style: {
        flex: `${left} 1 0`
      }
    }) : null));
  }
}

/** Drag a box over the player to mark what must stay in frame (Sizes). Fractions of the frame. */
export class SafeZoneLayer extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      drag: null
    });
    _defineProperty(this, "el", /*#__PURE__*/React.createRef());
    _defineProperty(this, "frac", e => {
      const b = this.el.current.getBoundingClientRect();
      return {
        x: clamp((e.clientX - b.left) / b.width, 0, 1),
        y: clamp((e.clientY - b.top) / b.height, 0, 1)
      };
    });
    _defineProperty(this, "down", e => {
      if (!this.props.drawing) return;
      e.preventDefault();
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch (er) {/* fine */}
      const p = this.frac(e);
      this.setState({
        drag: {
          a: p,
          b: p
        }
      });
    });
    _defineProperty(this, "move", e => {
      if (this.state.drag) this.setState({
        drag: {
          ...this.state.drag,
          b: this.frac(e)
        }
      });
    });
    _defineProperty(this, "up", () => {
      const d = this.state.drag;
      if (!d) return;
      this.setState({
        drag: null
      });
      const x = Math.min(d.a.x, d.b.x),
        y = Math.min(d.a.y, d.b.y),
        w = Math.abs(d.a.x - d.b.x),
        h = Math.abs(d.a.y - d.b.y);
      if (w < 0.02 || h < 0.02) {
        this.props.onRect(null);
        return;
      }
      this.props.onRect({
        x: +x.toFixed(4),
        y: +y.toFixed(4),
        w: +w.toFixed(4),
        h: +h.toFixed(4)
      });
    });
  }
  render() {
    const {
      rect,
      drawing
    } = this.props;
    const d = this.state.drag;
    const live = d ? {
      x: Math.min(d.a.x, d.b.x),
      y: Math.min(d.a.y, d.b.y),
      w: Math.abs(d.a.x - d.b.x),
      h: Math.abs(d.a.y - d.b.y)
    } : rect;
    return /*#__PURE__*/React.createElement("div", {
      ref: this.el,
      className: "cx-safe" + (drawing ? " drawing" : ""),
      onPointerDown: this.down,
      onPointerMove: this.move,
      onPointerUp: this.up,
      onPointerCancel: this.up
    }, live && live.w > 0 ? /*#__PURE__*/React.createElement("span", {
      className: "box",
      style: {
        left: `${live.x * 100}%`,
        top: `${live.y * 100}%`,
        width: `${live.w * 100}%`,
        height: `${live.h * 100}%`
      }
    }) : null);
  }
}